import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2, Globe, Lock, DollarSign, Gift } from 'lucide-react';

interface SaveModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: SaveModelData) => Promise<void>;
  initialTitle?: string;
  initialDescription?: string;
  initialIsPublic?: boolean;
  initialDownloadPrice?: number | null; // in dollars
  isNewModel?: boolean;
  isSaving?: boolean;
}

export interface SaveModelData {
  title: string;
  description: string;
  isPublic: boolean;
  commitMessage?: string;
  downloadPrice?: number | null; // in dollars, null = free
}

export function SaveModelDialog({
  open,
  onOpenChange,
  onSave,
  initialTitle = '',
  initialDescription = '',
  initialIsPublic = true,
  initialDownloadPrice = null,
  isNewModel = true,
  isSaving = false,
}: SaveModelDialogProps) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [isPublic, setIsPublic] = useState(initialIsPublic);
  const [commitMessage, setCommitMessage] = useState('');
  const [isPaid, setIsPaid] = useState(initialDownloadPrice != null);
  const [priceInput, setPriceInput] = useState(
    initialDownloadPrice != null ? initialDownloadPrice.toFixed(2) : '0.50',
  );
  const [error, setError] = useState<string | null>(null);

  // Reset form fields to the latest prop values each time the dialog opens
  const prevOpenRef = useRef(open);
  if (open && !prevOpenRef.current) {
    setTitle(initialTitle);
    setDescription(initialDescription);
    setIsPublic(initialIsPublic);
    setCommitMessage('');
    setIsPaid(initialDownloadPrice != null);
    setPriceInput(
      initialDownloadPrice != null ? initialDownloadPrice.toFixed(2) : '0.50',
    );
    setError(null);
  }
  prevOpenRef.current = open;

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    // Validate price if paid
    let downloadPrice: number | null = null;
    if (isPaid) {
      const parsed = parseFloat(priceInput);
      if (isNaN(parsed) || parsed < 0.5) {
        setError('Download price must be at least $0.50');
        return;
      }
      // Round to 2 decimal places
      downloadPrice = Math.round(parsed * 100) / 100;
    }

    setError(null);

    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        isPublic,
        commitMessage: commitMessage.trim() || undefined,
        downloadPrice,
      });
      onOpenChange(false);
    } catch {
      setError('Failed to save model. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-background sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {isNewModel ? 'Save New Model' : 'Save Changes'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isNewModel
              ? 'Give your model a name and choose visibility settings.'
              : 'Add a commit message to describe your changes.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Title */}
          <div className="grid gap-2">
            <Label htmlFor="title" className="text-muted-foreground">
              Title *
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="My Awesome Model"
              className="border-border bg-muted text-foreground placeholder:text-muted-foreground/50 focus:border-foreground"
            />
          </div>

          {/* Description */}
          <div className="grid gap-2">
            <Label htmlFor="description" className="text-muted-foreground">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this model does..."
              rows={3}
              className="resize-none border-border bg-muted text-foreground placeholder:text-muted-foreground/50 focus:border-foreground"
            />
          </div>

          {/* Commit message (for existing models) */}
          {!isNewModel && (
            <div className="grid gap-2">
              <Label htmlFor="commitMessage" className="text-muted-foreground">
                Commit Message
              </Label>
              <Input
                id="commitMessage"
                value={commitMessage}
                onChange={(e) => setCommitMessage(e.target.value)}
                placeholder="What changed? (optional)"
                className="border-border bg-muted text-foreground placeholder:text-muted-foreground/50 focus:border-foreground"
              />
            </div>
          )}

          {/* Visibility */}
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="flex items-center gap-3">
              {isPublic ? (
                <Globe className="h-5 w-5 text-sky-500" />
              ) : (
                <Lock className="h-5 w-5 text-muted-foreground" />
              )}
              <div className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  {isPublic ? 'Public' : 'Private'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {isPublic
                    ? 'Anyone can view and fork this model'
                    : 'Only you can view this model'}
                </span>
              </div>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={setIsPublic}
              className="data-[state=checked]:bg-sky-500"
            />
          </div>

          {/* Download Price */}
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isPaid ? (
                  <DollarSign className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Gift className="h-5 w-5 text-muted-foreground" />
                )}
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    {isPaid ? 'Paid download' : 'Free download'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {isPaid
                      ? 'Buyers pay via Stripe before downloading'
                      : 'Anyone can download for free'}
                  </span>
                </div>
              </div>
              <Switch
                checked={isPaid}
                onCheckedChange={setIsPaid}
                className="data-[state=checked]:bg-emerald-500"
              />
            </div>

            {isPaid && (
              <div className="mt-4">
                <Label
                  htmlFor="price"
                  className="mb-1.5 block text-xs text-muted-foreground"
                >
                  Price (USD, min $0.50)
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="price"
                    type="number"
                    min="0.50"
                    step="0.01"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    className="border-border bg-muted pl-7 text-foreground focus:border-foreground"
                    placeholder="0.50"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            className="text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !title.trim()}
            className="bg-foreground text-background hover:bg-foreground/90"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : isNewModel ? (
              'Save Model'
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
